'use client';

import { Spinner } from '@common/components';
import { CustomEditor, type Editor } from '@common/components/heavy';
import { SvgMailSend } from '@common/icon';
import { cn, CustomError } from '@core/utils';
import type { SaveWelloCommentResponse } from '@generated/auth';
import { useSuspenseQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Fragment, Suspense, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import type { COMMENT_TYPE } from '@/api';
import { UnauthQueryOptions } from '@/api';
import { BottomNavPortal } from '@/app/_components/BottomNavPortal';
import { ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useAuthStore } from '@/stores/AuthStore';

import { usePostCommentMutation } from './_hooks/usePostCommentMutation';
import { usePutCommentMutation } from './_hooks/usePutCommentMutation';

export type CommentFieldSubmitResult =
  | {
      /** 신규 댓글/답글 작성 완료 */
      type: 'create';
      /** 포인트, 신규 댓글 id 등 서버 응답 원문 */
      response?: SaveWelloCommentResponse;
    }
  | {
      /** 기존 댓글 수정 완료 */
      type: 'edit';
      /** 수정 후 스크롤 복원을 위한 댓글 id */
      commentId: number;
    };

interface CommentFieldContentProps {
  /** mount 직후 포커스를 강제할지 여부 */
  initOnFocus?: boolean;
  /** 답글 멘션 대상 닉네임 */
  replyTo?: string;
  /** 댓글이 달리는 원문/정책/게시글 id */
  contentId: number | string;
  /** 댓글 API 분기를 위한 도메인 타입 */
  commentType: COMMENT_TYPE;
  /** 답글일 때 최상위 댓글 id */
  rootCommentId?: number;
  /** 답글일 때 직접 부모 댓글 id */
  parentCommentId?: number;
  /** 수정 모드일 때 수정 대상 댓글 id */
  editingCommentId?: number;
  /** 수정 모드일 때 editor에 넣어둘 기존 본문 */
  editingContents?: string;
  /** 외부에서 강제로 포커스를 다시 주기 위한 카운터 */
  focusTrigger?: number;
  /** 로그인 여부에 따라 포커스 동작을 달리하기 위한 내부 값 */
  isLogin: boolean;
  /** 작성/수정 완료 후 상위 컴포넌트에 결과를 전달한다 */
  onSuccess?: (result: CommentFieldSubmitResult) => void;
  /** 입력창 상단 border 노출 여부 */
  topDivider?: boolean;
  /** 화면 하단 sticky 고정 여부 */
  sticky?: boolean;
  /** 화면별 wrapper 스타일 주입 */
  className?: string;
  /** 입력 필드 위에 같은 sticky 컨테이너 안에 함께 렌더할 슬롯 (예: 정책 상세의 3버튼) */
  topSlot?: ReactNode;
}

const CommentFieldContent = ({
  initOnFocus,
  replyTo,
  contentId,
  commentType,
  rootCommentId,
  parentCommentId,
  editingCommentId,
  editingContents,
  focusTrigger,
  isLogin,
  onSuccess,
  topDivider = true,
  sticky = false,
  className,
  topSlot,
}: CommentFieldContentProps) => {
  const router = useCustomRouter();
  // editingCommentId 존재 여부로 create/edit submit 경로를 분기한다.
  const isEditing = !!editingCommentId;

  // 답글은 에디터 맨 앞의 멘션 prefix를 보호 텍스트처럼 유지한다.
  const startText = replyTo ? `@${replyTo}` : '';

  const protectedPrefix = startText
    ? `<p class="min-h-24 typo-body4 font-400"><span types="listItem,starterKit" style="color: rgb(51, 102, 255)">${startText}</span>&nbsp;</p>`
    : null;

  const tempContentForProtectedPrefix = useRef('');
  const editorRef = useRef<Editor | null>(null);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  // reply/edit 대상이 바뀌면 동일한 입력창이라도 재초기화가 필요하다.
  const contentResetKey = `${editingCommentId ?? 'new'}:${replyTo ?? ''}:${editingContents ?? ''}`;

  const handleSubmitDisabled = (editor: Editor) => {
    let text = editor.getText({ blockSeparator: '\n' });

    if (startText && text.startsWith(startText)) {
      text = text.slice(startText.length + 1);
    }

    setIsSubmitDisabled(!text.trim().length);
  };

  const initContent = (editor: Editor) => {
    // 수정 모드가 우선이고, 아니면 답글 prefix 또는 빈 값으로 초기화한다.
    if (editingContents) {
      editor.commands.setContent(editingContents);

      return;
    }

    if (protectedPrefix) {
      tempContentForProtectedPrefix.current = protectedPrefix;
      editor.commands.setContent(protectedPrefix);
    } else {
      editor.commands.setContent('');
    }
  };

  useEffect(() => {
    const { current: editor } = editorRef;

    if (!editor) return;

    // 답글/수정 대상이 바뀌면 에디터 내용을 현재 모드 기준으로 다시 맞춘다.
    initContent(editor);
    handleSubmitDisabled(editor);

    if (initOnFocus || focusTrigger !== undefined) {
      editor.commands.focus();
    }
    // initContent/handleSubmitDisabled는 매 렌더 새 reference라 deps에 두지 않는다.
    // 함수가 실제로 의존하는 값(startText, editingContents, protectedPrefix)을 직접 추적.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contentResetKey,
    focusTrigger,
    initOnFocus,
    startText,
    editingContents,
    protectedPrefix,
  ]);

  const postComment = usePostCommentMutation({
    commentType,
    contentId,
    parentCommentId,
    rootCommentId,
    onSuccess: (response) => {
      const { current: editor } = editorRef;

      if (!editor) throw new CustomError();

      // 작성 성공 후에는 현재 모드 기준 초기값으로 입력창을 되돌린다.
      initContent(editor);
      handleSubmitDisabled(editor);
      onSuccess?.({
        type: 'create',
        response,
      });

      setTimeout(() => {
        editor.commands.focus();
      }, 100);
    },
  });
  const putComment = usePutCommentMutation({
    commentId: editingCommentId ?? 0,
    onSuccess: () => {
      onSuccess?.({
        type: 'edit',
        commentId: editingCommentId ?? 0,
      });
    },
  });

  const handleFocus = () => {
    if (isLogin) return;

    // 비로그인 상태에서 입력창 포커스 시도는 로그인으로 유도한다.
    const url = new URL(window.location.href);

    router.push(
      ROUTES.LOGIN.withSearchParams({
        searchParams: { redirect: url.pathname + url.search },
      }),
    );
  };

  const isPending = postComment.isPending || putComment.isPending;
  const canSubmit = !isPending && !isSubmitDisabled;

  return (
    <div
      className={cn(
        'w-full bg-Gray-white',
        topSlot ? 'flex flex-col gap-16 px-16 pb-20 pt-12' : 'p-16',
        { 'border-t border-Gray-200': topDivider },
        sticky && 'z-15 sticky bottom-0',
        className,
      )}
    >
      {topSlot}
      <CustomEditor.Provider
        placeholder="댓글을 입력해주세요"
        onCreate={({ editor }) => {
          initContent(editor);

          if (initOnFocus) {
            editor.commands.focus();
          }

          editorRef.current = editor;

          handleSubmitDisabled(editor);
        }}
        onFocus={handleFocus}
        onUpdate={({ editor }) => {
          if (protectedPrefix && startText) {
            const content = editor.getHTML();

            if (!content.includes(startText) || !content.includes('&nbsp;')) {
              return editor.commands.setContent(
                tempContentForProtectedPrefix.current || protectedPrefix,
              );
            }

            tempContentForProtectedPrefix.current = content;
          }

          handleSubmitDisabled(editor);
        }}
      >
        <form
          className="flex min-h-40 w-full items-end gap-8 rounded-large bg-Gray-50 px-16 py-4 backdrop-blur-sm"
          onSubmit={(e) => {
            e.preventDefault();

            const { current: editor } = editorRef;

            if (!editor) throw new CustomError();

            let text = editor.getText({ blockSeparator: '\n' });

            if (startText) {
              text = text.replace(startText, '');
            }

            text = text.trim();

            if (!text.length) return;

            if (isEditing && editingCommentId) {
              putComment.mutate(text);

              return;
            }

            postComment.mutate(text);
          }}
        >
          <CustomEditor.Contents className="!max-h-96 flex-1 self-center !p-0 font-400 text-Gray-900 hide-scrollbar typo-body4" />
          <button
            type="submit"
            aria-label="댓글 전송"
            disabled={!canSubmit}
            className="flex shrink-0 items-center self-end py-10 disabled:cursor-not-allowed"
          >
            {isPending ? (
              <Spinner size={20} />
            ) : (
              <SvgMailSend
                className={cn(
                  'size-20',
                  canSubmit ? 'text-B-500' : 'text-Gray-black',
                )}
              />
            )}
          </button>
        </form>
      </CustomEditor.Provider>
    </div>
  );
};

const ReplyCommentField = (
  props: Omit<CommentFieldContentProps, 'replyTo' | 'parentCommentId'> & {
    /** 닉네임 조회에 사용할 부모 댓글 id */
    parentCommentId: number;
  },
) => {
  // replyTo가 직접 내려오지 않은 경우 부모 댓글 작성자 닉네임을 조회해 멘션 prefix를 만든다.
  const { data: nickname } = useSuspenseQuery({
    ...UnauthQueryOptions[
      'get:/wello/v2/allows/wello-comment/{welloCommentId}'
    ]({
      path: {
        welloCommentId: props.parentCommentId,
      },
    }),
    select: (data) => data.context?.nickName,
  });

  return <CommentFieldContent {...props} replyTo={nickname} />;
};

const FixedArea = () => <div className="h-81 w-full" />;

export interface CommentFieldProps extends Omit<
  CommentFieldContentProps,
  'isLogin'
> {
  /** 글로벌 하단 내비게이션 영역으로 portal 렌더링할지 여부 */
  withPortal?: boolean;
}

export const CommentField = ({
  parentCommentId,
  replyTo,
  withPortal = false,
  ...restProps
}: CommentFieldProps) => {
  const isLogin = useAuthStore((state) => state.isLogin);

  // 화면에 따라 본문 하단 sticky 또는 글로벌 하단 portal 중 하나를 선택한다.
  const Container = withPortal ? BottomNavPortal : Fragment;

  return (
    <Container>
      {isLogin !== undefined ? (
        <ErrorBoundary fallback={null}>
          <Suspense fallback={<FixedArea />}>
            {parentCommentId && !replyTo && !restProps.editingCommentId ? (
              <ReplyCommentField
                {...restProps}
                isLogin={isLogin}
                parentCommentId={parentCommentId}
              />
            ) : (
              <CommentFieldContent
                {...restProps}
                isLogin={isLogin}
                parentCommentId={parentCommentId}
                replyTo={replyTo}
              />
            )}
          </Suspense>
        </ErrorBoundary>
      ) : (
        <FixedArea />
      )}
    </Container>
  );
};
